import type { Dispatch } from '@reduxjs/toolkit'
import type { Business, User } from '@pbt/pbt-ui-components'

import type { RefundInvoice } from '~/api/graphql/generated/types'
import { triggerErrorDoggo } from '~/utils/errorDoggoUtils'

import { RefundFlowState } from '../constants/refundDialogV2Constants'
import {
  trackUnlinkedRefundInitiationError,
  trackUnlinkedRefundProcessing,
} from './refundDialogTrackingUtils'
import {
  getGraphQLRefundErrorMessage,
  getRefundInitiationFailureMessage,
  triggerErrorDoggoForRefundInitiationErrorIfNeeded,
} from './refundErrorMapping'

interface InitiateRefundTransactionDependencies {
  currentBusiness?: Business | null
  currentUser?: User | null
  dispatch: (action: any) => void
  initiateRefundTransactionMutation: (options: {
    variables: {
      input: {
        amount: number
        externalDeviceId: string | null
        originalPaymentId: string
        refundInvoiceId: string
      }
    }
  }) => Promise<any>
  reduxDispatch: Dispatch<any>
  refundAmount: number
  refundInvoice: RefundInvoice
  selectedPaymentId: string
  selectedPaymentTerminal: string | null
  setIsLoading: (loading: boolean) => void
}

/**
 * Handles the refund transaction mutation with error handling.
 * Extracted to reduce cognitive complexity in useMemo.
 */
export const initiateRefundTransaction = async (
  deps: InitiateRefundTransactionDependencies,
): Promise<void> => {
  const {
    dispatch,
    reduxDispatch,
    setIsLoading,
    initiateRefundTransactionMutation,
    refundAmount,
    selectedPaymentTerminal,
    selectedPaymentId,
    refundInvoice,
    currentUser,
    currentBusiness,
  } = deps

  try {
    const result = await initiateRefundTransactionMutation({
      variables: {
        input: {
          amount: refundAmount,
          originalPaymentId: selectedPaymentId,
          refundInvoiceId: refundInvoice.id,
          externalDeviceId: selectedPaymentTerminal,
        },
      },
    })

    if (!result?.data?.initiateRefundTransaction || result.errors) {
      dispatch({ type: 'CANCEL_TRANSACTION' })
      // Check for specific GraphQL error types to show appropriate message
      const graphQLErrorMessage = getGraphQLRefundErrorMessage(result.errors)
      const errorMessage =
        graphQLErrorMessage || getRefundInitiationFailureMessage()
      const backendErrorMessage = result.errors?.[0]?.message || errorMessage

      // Track unlinked refund initiation error (GraphQL error)
      trackUnlinkedRefundInitiationError({
        refundInvoice,
        selectedPaymentId,
        refundAmount,
        businessId: currentBusiness?.id || '',
        terminalId: selectedPaymentTerminal,
        failureReason: 'GRAPHQL_ERROR',
        errorMessage: backendErrorMessage,
      })
      triggerErrorDoggoForRefundInitiationErrorIfNeeded({
        errors: result.errors,
        dispatch: reduxDispatch,
        backendErrorMessage,
        currentUser,
        currentBusiness,
      })
      dispatch({
        type: 'SET_REFUND_ERROR',
        payload: {
          message: errorMessage,
          severity: 'error',
          status: 500,
        },
      })
    } else {
      const transactionId = result.data.initiateRefundTransaction.id
      dispatch({
        type: 'SET_STATE',
        payload: RefundFlowState.REFUND_PROCESSING,
      })
      dispatch({
        type: 'SET_TRANSACTION_ID',
        payload: transactionId,
      })
      // Store externalDeviceId from mutation response if available
      if (result.data.initiateRefundTransaction.externalDeviceId) {
        dispatch({
          type: 'SET_EXTERNAL_DEVICE_ID',
          payload: result.data.initiateRefundTransaction.externalDeviceId,
        })
      }
      trackUnlinkedRefundProcessing({
        refundInvoice,
        selectedPaymentId,
        refundAmount,
        businessId: currentBusiness?.id || '',
        terminalId: selectedPaymentTerminal,
        transactionId,
      })
    }
  } catch (err) {
    dispatch({ type: 'CANCEL_TRANSACTION' })
    const errorMessage = getRefundInitiationFailureMessage()
    const backendErrorMessage = (err as any)?.message || errorMessage

    // Track unlinked refund initiation error (network/exception)
    trackUnlinkedRefundInitiationError({
      refundInvoice,
      selectedPaymentId,
      refundAmount,
      businessId: currentBusiness?.id || '',
      terminalId: selectedPaymentTerminal,
      failureReason: 'INITIATION_ERROR',
      errorMessage: backendErrorMessage,
    })

    triggerErrorDoggo(
      reduxDispatch,
      backendErrorMessage,
      500,
      currentUser,
      currentBusiness,
    )
    dispatch({
      type: 'SET_REFUND_ERROR',
      payload: {
        message: errorMessage,
        severity: 'error',
        status: 500,
      },
    })
  } finally {
    setIsLoading(false)
  }
}
